<script lang="ts">
  import { onMount } from "svelte"
  import Sortable from "sortablejs"
  import { xhr, toast, sortElementsForSortable, wait } from "$app/utils"
  import { ChevronDown, ChevronRight } from "lucide-svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Task from "./Task.svelte"
  import { appStore } from "$app/store.svelte"
  import { slide } from "svelte/transition"

  interface Props {
    assignee: AssigneeType
    editable?: boolean
  }

  let { assignee, editable = true }: Props = $props()

  const plan = appStore.plan
  let expanded = $state(true)

  function updateTasks(newTasks: TaskType[]): void {
    newTasks.forEach((task) => {
      const idx = appStore.tasks.findIndex((t) => task.id === t.id)
      appStore.tasks[idx] = {
        ...appStore.tasks[idx],
        ...task
      }
    })
  }

  async function saveTasks(
    newTasks: TaskType[],
    assignee: AssigneeType
  ): Promise<void> {
    try {
      const resp = await xhr.post(
        `/plans/${plan.id}/tasks/sort_by_assignee.json`,
        {
          tasks: newTasks.map((t) => t.id),
          assignee_id: assignee.id,
          assignee_type: assignee.type
        }
      )
      updateTasks(resp.data)
    } catch (e) {
      const tempTasks = [...appStore.tasks]
      appStore.tasks = []
      await wait(10)
      appStore.tasks = tempTasks
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating the sort order", "error")
    }
  }

  let sortableSection: HTMLElement = $state()
  let startId: number
  /**
   * Creates sortable for tasks
   */
  function setSortable(): void {
    if (!sortableSection) return
    const sort = Sortable.create(sortableSection, {
      animation: 200,
      group: "Task",
      scrollSensitivity: 150,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      filter: ".no-drag",
      emptyInsertThreshold: 20,
      onEnd: async (e: EventSortType) => {
        sort.options.sort = false

        if (
          e.from.dataset.assignee_id === e.to.dataset.assignee_id &&
          e.from.dataset.assignee_type === e.to.dataset.assignee_type &&
          e.newIndex === e.oldIndex
        )
          return

        // Move from other container
        if (
          e.from.dataset.assignee_id === e.to.dataset.assignee_id &&
          e.from.dataset.assignee_type === e.to.dataset.assignee_type
        ) {
          // Same container
          const currentTasks = appStore.tasks
            .filter(
              (t) =>
                assignee.id === t.assignee_id &&
                assignee.type === t.assignee_type
            )
            .sort((a, b) => a.assignee_position - b.assignee_position)

          const newTasks = sortElementsForSortable(
            currentTasks,
            e.newIndex,
            e.oldIndex
          ) as TaskType[]

          await saveTasks(newTasks, assignee)
        } else {
          const assignee_id = parseInt(e.to.dataset.assignee_id)
          const assignee_type = e.to.dataset.assignee_type
          const currentTasks = appStore.tasks
            .filter(
              (t) =>
                t.assignee_id === assignee_id &&
                t.assignee_type === assignee_type
            )
            .sort((a, b) => a.parent_position - b.parent_position)

          const currentTask = appStore.tasks.find(
            (t) => t.id === appStore.draggedElement.id
          )

          const newTasks = [
            ...currentTasks.slice(0, e.newIndex),
            currentTask,
            ...currentTasks.slice(e.newIndex)
          ]

          await saveTasks(newTasks, {
            id: assignee_id,
            type: assignee_type
          } as AssigneeType)
        }

        sort.options.sort = true
      },
      onStart: (evt: EventSortType) => {
        appStore.draggedElement = {
          id: parseInt(evt.item.dataset.id),
          type: "Task",
          assignee_id: assignee.id,
          assignee_type: assignee.type
        } as draggedElemType
      }
    })
  }

  let mounted = $state(false)
  $effect(() => {
    if (mounted && editable && expanded) {
      setTimeout(() => {
        setSortable()
      }, 50)
    }
  })

  onMount(() => (mounted = true))
</script>

{#if mounted}
  {@const tTasks = appStore.tasks
    .filter(
      (t) => t.assignee_id === assignee.id && t.assignee_type === assignee.type
    )
    .sort((a, b) => a.assignee_position - b.assignee_position)}

  <div class="mt-6 mb-2 sm:py-2" class:hover:cursor-pointer={tTasks.length > 0}>
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <div
      class="flex justify-start"
      role="button"
      tabindex="0"
      onclick={() => {
        if (tTasks.length) {
          expanded = !expanded
        }
      }}
    >
      <div
        class={[
          "flex items-center pr-1 gap-1 rotate-icon",
          expanded ? "open" : ""
        ]}
      >
        {#if tTasks.length > 0}
          <ChevronRight class="text-gray-500 size-6 rotate-icon-90" />
        {/if}
      </div>
      <div class="text-2xl">
        {assignee.name}
      </div>
    </div>
  </div>

  <div class="relative pl-8">
    {#if expanded}
      <section
        style="min-height:40px"
        bind:this={sortableSection}
        data-assignee_id={assignee.id}
        data-assignee_type={assignee.type}
        transition:slide
      >
        {#each tTasks as task, idx (task.id)}
          <div data-id={task.id}>
            <Task {task} />
          </div>
        {:else}
          <div
            class="w-full border border-gray-200 shadow py-2 px-6 no-drag text-gray-600"
          >
            No Tasks Found for this Assignee
          </div>
        {/each}
      </section>
    {/if}
  </div>
{/if}
